import { Archive } from 'lucide-react';
import { WIKI_MANAGE_COPY } from '../modals/wikiModalCopy';
import { wikiKeys } from './wikiData';

const enc = encodeURIComponent;

/**
 * `wikiUi.openModal('confirm', …)` options for archiving a category or a
 * subcategory (#872). The body comes from `GET …/archive-preview`, which
 * counts exactly what the cascade will archive; `onArchived` runs after the
 * modal closed (navigate away, toast, refresh). Admin-only, like the route.
 */
export function archiveCategoryConfirm({ category, onArchived }) {
    const id = category.category_id || category.id;
    return {
        title: WIKI_MANAGE_COPY.archiveCategoryTitle(category.name || ''),
        loadBody: (client) => client
            .get(`/wiki/categories/${enc(id)}/archive-preview`)
            .then((preview) => WIKI_MANAGE_COPY.archiveCategoryBody(preview)),
        confirmLabel: WIKI_MANAGE_COPY.archive,
        icon: Archive,
        danger: true,
        requires: 'admin',
        action: 'wiki.archive_category',
        testId: 'wiki-archive-category-modal',
        run: (client) => client.post(`/wiki/categories/${enc(id)}/archive`, {}),
        errorCopy: WIKI_MANAGE_COPY.archiveCategoryErrors,
        onDone: onArchived,
    };
}

/** The same for one page: anyone who can edit it (wiki:write + edit access). */
export function archivePageConfirm({ page, onArchived }) {
    return {
        title: WIKI_MANAGE_COPY.archivePageTitle(page.title || 'Untitled page'),
        body: WIKI_MANAGE_COPY.archivePageBody,
        confirmLabel: WIKI_MANAGE_COPY.archive,
        icon: Archive,
        danger: true,
        requires: 'edit',
        action: 'wiki.archive_page',
        testId: 'wiki-archive-page-modal',
        run: (client) => client.post(`/wiki/pages/${enc(page.page_id)}/archive`, {}),
        errorCopy: WIKI_MANAGE_COPY.archivePageErrors,
        onDone: onArchived,
    };
}

/**
 * Refresh every Wiki query after an archive, except the archived item's own
 * (`gone`, a key prefix such as `wikiKeys.page(id)`): its view is still mounted
 * while the app navigates away, and refetching it would only answer 403/404.
 * Those entries are dropped instead, so a later visit asks the server again.
 */
export async function refreshAfterArchive(queryClient, gone) {
    if (!queryClient) return;
    const under = (key) => gone.every((part, i) => key[i] === part);
    // Mark the archived item's own entries stale without fetching them now…
    await queryClient.invalidateQueries({ queryKey: gone, refetchType: 'none' });
    await queryClient.invalidateQueries({ queryKey: wikiKeys.all(), predicate: (query) => !under(query.queryKey) });
    // …and drop them once nothing observes them (the navigation has committed).
    // Removing a query that is still observed would fetch it again; if the
    // view hasn't unmounted yet, the entry simply stays stale.
    setTimeout(() => queryClient.removeQueries({ queryKey: gone, type: 'inactive' }), 0);
}
